/**
 * SchemeDiversity.jsx  — Page shell only
 * Handles: session restore, Redux sync, header, title bar
 * All content delegated to SchemeDiversityView
 */

import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch }   from 'react-redux';
import { Navigate }                   from 'react-router-dom';

import KPIHeader                from '../../Header/KPIHeader';
import KPISubHeader             from '../../Header/SubHeader/KPISubHeader';
import SchemeDiversityDefitions from '../../Defintions/SchemeDiversityDefitions';
import SchemeDiversityView from '../../KPI/Scheme Diversity/SchemeDiversityView';

import { getSession }                           from '../../utils/session';
import { setProjectDetails, setProjectsData, setEmail as setReduxEmail } from '../../userSlice';

const SchemeDiversity = () => {
  const dispatch = useDispatch();

  const reduxEmail       = useSelector(s => s.user.email);
  const reduxProjectName = useSelector(s => s.user.projectName);
  const reduxPid         = useSelector(s => s.user.projectId);
  const reduxViewIds     = useSelector(s => s.user.viewIds);
  const isSuperuser      = useSelector(s => s.user.isSuperuser);

  const [email,       setEmail]       = useState(reduxEmail       || '');
  const [projectName, setProjectName] = useState(reduxProjectName || '');
  const [pid,         setPid]         = useState(reduxPid         || '');
  const [viewIds,     setViewIds]     = useState(reduxViewIds     || []);
  const [loading,     setLoading]     = useState(true);

  useEffect(() => {
    // If Redux already has data (normal SPA navigation) — use it directly
    if (reduxEmail && reduxPid) {
      setEmail(reduxEmail);
      setPid(reduxPid);
      setProjectName(reduxProjectName || '');
      setViewIds(reduxViewIds || []);
      setLoading(false);
      return;
    }

    // Hard refresh / direct URL — restore from sessionStorage
    const session = getSession();
    if (session?.email) {
      setEmail(session.email);
      setPid(session.projectId    || '');
      setProjectName(session.projectName || '');
      setViewIds(session.viewIds  || []);

      dispatch(setProjectDetails({
        email:            session.email,
        projectName:      session.projectName,
        projectId:        session.projectId,
        organizationName: session.organizationName,
      }));

      if (session.projectsData && reduxViewIds.length === 0) {
        dispatch(setReduxEmail(session.email));
        dispatch(setProjectsData(session.projectsData));
      }
    }
    setLoading(false);
  }, [dispatch, reduxEmail, reduxPid, reduxProjectName, reduxViewIds]);

  // Keep local state in sync when user switches project
  useEffect(() => {
    if (reduxPid)             setPid(reduxPid);
    if (reduxViewIds?.length) setViewIds(reduxViewIds);
    if (reduxProjectName)     setProjectName(reduxProjectName);
  }, [reduxPid, reduxViewIds, reduxProjectName]);

  if (loading) return null;
  if (!email)  return <Navigate to="/" replace />;

  return (
    <div className="bg-gray-50 min-h-screen">
      <KPIHeader />
      <KPISubHeader />
      <div className="mt-32">
        

        {/* Project title bar */}
        <div className="px-6 py-2 flex items-center justify-between border-b border-gray-200 mb-3 bg-white rounded-lg shadow-sm">
          <div className="flex items-center space-x-4">
            <div className="bg-blue-100 p-2 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-800 truncate max-w-xs md:max-w-md">
              {projectName}
            </h1>
            {isSuperuser && (
              <span className="text-xs font-bold bg-purple-100 text-purple-700 border border-purple-300 px-2 py-0.5 rounded-full">
                SUPERUSER
              </span>
            )}
          </div>

          <span className="bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-800 text-sm font-semibold px-4 py-2 rounded-full shadow-sm flex items-center">
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            PID: {pid}
          </span>
        </div>

        {/* All gated content lives here */}
        <SchemeDiversityView pid={pid} viewIds={viewIds} isSuperuser={isSuperuser} />
      </div>
    </div>
  );
};

export default SchemeDiversity;